import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/auth/AuthContext';
import { trpc } from '@/trpc/client';
import { unifiedDiff } from './diff';

interface CommonRow {
  version: string;
  text: string;
  updatedAt: string;
  updatedBy?: { email?: string } | null;
}

interface CategoryRow {
  version: string;
  body: string;
  displayName?: string;
  classifierHint?: string;
  updatedAt: string;
  updatedBy?: { email?: string } | null;
}

interface Props {
  kind: 'common' | 'category';
}

export function PromptHistoryModal({ kind }: Props): JSX.Element {
  if (kind === 'common') return <CommonHistory />;
  return <CategoryHistory />;
}

function CommonHistory(): JSX.Element {
  const { user } = useAuth();
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const { data: current } = trpc.prompts.common.get.useQuery();
  const { data, isLoading } = trpc.prompts.common.history.useQuery({});
  const [target, setTarget] = useState<{ version: string; text: string } | null>(null);
  const rollback = trpc.prompts.common.rollback.useMutation({
    onSuccess: async () => {
      await utils.prompts.common.get.invalidate();
      setTarget(null);
      navigate('/prompts/common');
    },
  });
  const canRollback = user?.role === 'editor' || user?.role === 'admin';

  return (
    <Frame title="共通プロンプト の履歴" onClose={() => navigate('/prompts/common')}>
      {isLoading ? (
        <p>読み込み中...</p>
      ) : (
        <HistoryTable
          rows={(data?.items ?? []) as CommonRow[]}
          canRollback={canRollback}
          onPick={(row) => setTarget({ version: row.version, text: row.text })}
          excerpt={(row) => row.text}
        />
      )}
      {target && (
        <RollbackConfirm
          version={target.version}
          before={current?.text ?? ''}
          after={target.text}
          isPending={rollback.isPending}
          onCancel={() => setTarget(null)}
          onConfirm={() => rollback.mutate({ version: target.version })}
        />
      )}
    </Frame>
  );
}

function CategoryHistory(): JSX.Element {
  const params = useParams();
  const id = params.id ?? '';
  const { user } = useAuth();
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const { data: current } = trpc.prompts.category.get.useQuery({ id });
  const { data, isLoading } = trpc.prompts.category.history.useQuery({ id });
  const [target, setTarget] = useState<{ version: string; body: string } | null>(null);
  const rollback = trpc.prompts.category.rollback.useMutation({
    onSuccess: async () => {
      await utils.prompts.category.list.invalidate();
      await utils.prompts.category.get.invalidate({ id });
      setTarget(null);
      navigate(`/prompts/categories/${id}`);
    },
  });
  const canRollback = user?.role === 'editor' || user?.role === 'admin';

  return (
    <Frame
      title={`${current?.displayName ?? 'カテゴリ'} の履歴`}
      onClose={() => navigate(`/prompts/categories/${id}`)}
    >
      {isLoading ? (
        <p>読み込み中...</p>
      ) : (
        <HistoryTable
          rows={(data?.items ?? []) as CategoryRow[]}
          canRollback={canRollback}
          onPick={(row) => setTarget({ version: row.version, body: row.body })}
          excerpt={(row) => row.body}
        />
      )}
      {target && (
        <RollbackConfirm
          version={target.version}
          before={current?.body ?? ''}
          after={target.body}
          isPending={rollback.isPending}
          onCancel={() => setTarget(null)}
          onConfirm={() => rollback.mutate({ id, version: target.version })}
        />
      )}
    </Frame>
  );
}

function Frame({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black/30">
      <div className="w-[720px] rounded bg-white p-4 shadow">
        <header className="mb-3 flex items-center justify-between">
          <h2 className="text-base font-semibold">{title}</h2>
          <button type="button" className="rounded border px-2 py-1 text-sm" onClick={onClose}>
            閉じる
          </button>
        </header>
        {children}
      </div>
    </div>
  );
}

function HistoryTable<
  R extends { version: string; updatedAt: string; updatedBy?: { email?: string } | null },
>({
  rows,
  canRollback,
  onPick,
  excerpt,
}: {
  rows: R[];
  canRollback: boolean;
  onPick: (row: R) => void;
  excerpt: (row: R) => string;
}): JSX.Element {
  return (
    <table className="min-w-full text-sm">
      <thead className="bg-gray-50 text-xs uppercase text-gray-600">
        <tr>
          <th className="px-2 py-1 text-left">version</th>
          <th className="px-2 py-1 text-left">更新日時</th>
          <th className="px-2 py-1 text-left">更新者</th>
          <th className="px-2 py-1 text-left">先頭 40 文字</th>
          <th className="px-2 py-1 text-left" />
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100">
        {rows.map((row) => (
          <tr key={row.version}>
            <td className="px-2 py-1 font-mono">{row.version}</td>
            <td className="px-2 py-1 text-xs text-gray-500">{row.updatedAt}</td>
            <td className="px-2 py-1 text-xs">{row.updatedBy?.email ?? '—'}</td>
            <td className="max-w-xs truncate px-2 py-1">{excerpt(row).slice(0, 40)}</td>
            <td className="px-2 py-1">
              {canRollback && (
                <button
                  type="button"
                  className="rounded border px-2 py-0.5 text-xs hover:bg-gray-50"
                  onClick={() => onPick(row)}
                >
                  このバージョンに戻す
                </button>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function RollbackConfirm({
  version,
  before,
  after,
  isPending,
  onCancel,
  onConfirm,
}: {
  version: string;
  before: string;
  after: string;
  isPending: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}): JSX.Element {
  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black/40">
      <div className="w-[600px] rounded bg-white p-4 shadow">
        <h3 className="mb-2 text-base font-semibold">{version} に戻す</h3>
        <div className="mb-3 max-h-64 overflow-auto rounded border bg-gray-50 p-2 font-mono text-xs">
          {unifiedDiff(before, after).map((line, idx) => (
            <div
              // biome-ignore lint/suspicious/noArrayIndexKey: diff lines have no stable id
              key={idx}
              className={
                line.kind === 'add' ? 'bg-green-100' : line.kind === 'remove' ? 'bg-red-100' : ''
              }
            >
              {line.kind === 'add' ? '+ ' : line.kind === 'remove' ? '- ' : '  '}
              {line.text}
            </div>
          ))}
        </div>
        <div className="flex justify-end gap-2">
          <button type="button" className="rounded border px-3 py-1 text-sm" onClick={onCancel}>
            キャンセル
          </button>
          <button
            type="button"
            className="rounded bg-blue-600 px-3 py-1 text-sm text-white"
            disabled={isPending}
            onClick={onConfirm}
          >
            ロールバックして反映
          </button>
        </div>
      </div>
    </div>
  );
}
